import { useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/auth-provider";
import { AssistantPanelHost } from "@/components/assistant/assistant-panel-host";
import { BillingDialogHost } from "@/components/billing/billing-dialog";
import { LowCreditBanner } from "@/components/billing/low-credit-banner";
import { CommandPalette } from "@/components/command-palette/command-palette";
import { LightboxHost } from "@/components/media/lightbox";
import { ProjectDialog } from "@/components/projects/project-dialog";
import { AppSidebar } from "@/components/shell/app-sidebar";
import { DotLoader } from "@/components/ui/spinner";
import { useCurrentProject } from "@/hooks/use-current-project";
import { useHotkeys } from "@/hooks/use-hotkeys";
import { useCan } from "@/hooks/use-permissions";
import {
	projectNavigation,
	projectPath,
	projectSettingsNavItem,
	workspaceNavigation,
} from "@/routes/app-navigation";
import { preloadWhenIdle } from "@/routes/lazy-page";
import { commandPaletteOpenAtom, projectDialogAtom } from "@/store/ui";
import { SyncProvider } from "@/sync/sync-provider";
import { AppShellSkeleton } from "./app-shell-skeleton";

export function ProtectedLayout() {
	const { user, workspace } = useAuth();
	if (!user || !workspace) return <AppShellSkeleton />;
	return (
		<SyncProvider
			key={workspace.id}
			userId={user.id}
			workspaceId={workspace.id}
			fallback={<AppShellSkeleton />}
		>
			<AppShell />
		</SyncProvider>
	);
}

function PageFallback() {
	return (
		<div className="flex flex-1 items-center justify-center text-muted-foreground">
			<DotLoader />
		</div>
	);
}

function AppShell() {
	const navigate = useNavigate();
	const { projectId } = useCurrentProject();
	const setPaletteOpen = useSetAtom(commandPaletteOpenAtom);
	const openProjectDialog = useSetAtom(projectDialogAtom);
	const canCreateProject = useCan("projects.create");

	useEffect(() => {
		preloadWhenIdle(
			[...projectNavigation, projectSettingsNavItem, ...workspaceNavigation].map(
				(item) => item.page,
			),
		);
	}, []);

	useHotkeys([
		{ keys: "mod+k", handler: () => setPaletteOpen((open) => !open) },
		{
			keys: "c",
			enabled: canCreateProject,
			handler: () => openProjectDialog({ mode: "create" }),
		},
		...workspaceNavigation.map((item) => ({
			keys: item.shortcut,
			handler: () => navigate(item.path),
		})),
		...[...projectNavigation, projectSettingsNavItem].map((item) => ({
			keys: item.shortcut,
			enabled: !!projectId,
			handler: () => projectId && navigate(projectPath(projectId, item.segment)),
		})),
	]);

	return (
		<div className="flex h-dvh bg-sidebar">
			<AppSidebar />
			<main className="my-2 mr-2 flex min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] border bg-card">
				<LowCreditBanner />
				<Suspense fallback={<PageFallback />}>
					<Outlet />
				</Suspense>
			</main>
			<AssistantPanelHost />
			<CommandPalette />
			<ProjectDialog />
			<BillingDialogHost />
			<LightboxHost />
		</div>
	);
}
